import {
	BreakpointAliasLegacy,
	FontWeightAliasLegacy,
	ShadowAliasLegacy,
	SpaceAliasLegacy,
} from "../theming/bca/bca-attributes";
import { Color, Size, Unit, Weight } from "./globals";
import { PageSectionTheme } from "../components/layout/PageSection/PageSection.types";
import { Palette } from "../functions/createPalette";
import { SignatureCardTheme } from "../lab/components/layout/cards/SignatureCard/SignatureCard.types";
import { RatingBadgeTheme } from "../components/data/badges/RatingBadge/RatingBadge.types";
import { LinkTheme } from "../components/actions/Link/Link.types";

/**
 * BorderWidth
 */
export type BorderWidthOptionsObject = Pick<Size<Unit>, "sm" | "md" | "lg">;
export type BorderWidthOptions = keyof BorderWidthOptionsObject;
export type BorderWidths = BorderWidthOptionsObject;

/**
 * Breakpoint
 */
export type BreakpointOptionsObject = Pick<
	Size<number>,
	"xs" | "sm" | "md" | "lg" | "xl" | "xxl"
>;
export type BreakpointOptions = keyof BreakpointOptionsObject;
export type Breakpoints = BreakpointOptionsObject;

/**
 * Color
 */
export type ColorPaletteLuminosityOptions =
	| 100
	| 200
	| 300
	| 400
	| 500
	| 600
	| 700
	| 800
	| 900;

export type ColorPaletteLuminosityNeutralOptions =
	| ColorPaletteLuminosityOptions
	| 1000
	| 1100;

export type ColorPalettePrimaryOptions = `${
	| "primary_accent"
	| "primary_1"}_${ColorPaletteLuminosityOptions}`;

export type ColorPaletteNeutralOptions =
	`neutral_${ColorPaletteLuminosityNeutralOptions}`;

export type ColorPaletteSemanticOptions = `semantic_${
	| "error"
	| "info"
	| "success"
	| "warning"}_${ColorPaletteLuminosityOptions}`;

/**
 * Color...
 * Roles
 */
export type ColorPaletteRoleBackgroundOptions = `bg_${
	| "dark"
	| "dark_variant"
	| "light"
	| "light_variant"
	| "white"}`;

export type ColorPaletteRoleForegroundBaseOptions = `fg_${"dark" | "light"}`;
export type ColorPaletteRoleForegroundContrastOptions = 1 | 2 | 3;

export type ColorPaletteRoleForegroundOptions =
	`${ColorPaletteRoleForegroundBaseOptions}_${ColorPaletteRoleForegroundContrastOptions}`;

export type ColorPaletteRoleCtaOptions = `cta_${
	| "active"
	| "default"
	| "disabled"
	| "focus"
	| "hover"}`;

export type ColorPaletteRoleOptions =
	| ColorPaletteRoleBackgroundOptions
	| ColorPaletteRoleForegroundOptions
	| ColorPaletteRoleCtaOptions;

export type ColorPaletteRoles = {
	[key in ColorPaletteRoleOptions]: ColorPaletteMandatoryOptions;
};

/**
 * Color...
 * Define the mandatory palette
 */
export type ColorPaletteMandatoryOptions =
	| "black"
	| "white"
	| ColorPalettePrimaryOptions
	| ColorPaletteNeutralOptions
	| ColorPaletteSemanticOptions;

export type ColorPaletteMandatory = {
	[key in ColorPaletteMandatoryOptions]: Color;
};

/**
 * Color...
 * Supporting palette
 */
export type ColorPaletteSupportingIndexOptions = 2 | 3 | 4 | 5 | 6;

export type ColorPaletteSupportingOptions =
	| `primary_${ColorPaletteSupportingIndexOptions}_${ColorPaletteLuminosityOptions}`
	| `secondary_${
			| 1
			| ColorPaletteSupportingIndexOptions}_${ColorPaletteLuminosityOptions}`
	| `tertiary_${
			| 1
			| ColorPaletteSupportingIndexOptions}_${ColorPaletteLuminosityOptions}`;

export type ColorPaletteSupporting = {
	[key in ColorPaletteSupportingOptions]?: Color;
};

/**
 * Color...
 * Palette implementation
 */
export type ColorPaletteOptions =
	| ColorPaletteMandatoryOptions
	| ColorPaletteSupportingOptions
	| ColorPaletteRoleOptions;

export type ColorPaletteFunctions = {
	hexFor(token: ColorPaletteOptions | undefined): string | undefined;
	hexForRole(token: ColorPaletteRoleOptions | undefined): string | undefined;
	hexForMandatory(
		token: ColorPaletteMandatoryOptions | undefined
	): string | undefined;
	hexForSupporting(
		token: ColorPaletteSupportingOptions | undefined
	): string | undefined;
};

/**
 * Every color palette needs to implement the ColorPalette type.
 */
export type ColorPalette = ColorPaletteMandatory & ColorPaletteSupporting;

/**
 * Fonts
 * Note: normal just satisfies the existing, legacy option. This hasn't been looked at yet,
 * so there's every chance new attributes/options will be defined. Need to communicate with
 * designers and determine other options.
 */
export type Fonts = {
	normal: string; // #Legacy
	body: string;
};

/**
 * FontWeights
 */
export type FontWeightOptionsObject = Pick<
	Weight,
	"lightest" | "light" | "regular" | "heavy" | "heaviest"
>;
export type FontWeightOptions = keyof FontWeightOptionsObject;
export type FontWeights = {
	// We need to temporarily place font weights in a values object, otherwise there
	// will be a clash between our 'light' and the legacy 'light' obj property.
	values: FontWeightOptionsObject;
};

/**
 * Radii
 */
export type RadiiOptionsObject = Pick<Size<Unit>, SpaceOptions>;
export type RadiiOptions = keyof RadiiOptionsObject;
export type Radii = RadiiOptionsObject;

/**
 * Shadows
 */
export type ShadowFormat = `${number}px ${number}px ${number}px rgba(0, 0, 0, ${
	| 0.1
	| 0.2})`;
export type ShadowOptions = `shadow_${100 | 200}`;
export type Shadows = {
	[key in ShadowOptions]: ShadowFormat;
};

/**
 * Space
 */
export enum SpaceOptionsEnum {
	"xxs" = "xxs",
	"xs" = "xs",
	"sm" = "sm",
	"md" = "md",
	"lg" = "lg",
	"xl" = "xl",
	"xxl" = "xxl",
	"xxxl" = "xxxl",
	"xxxl-2" = "xxxl-2",
	"xxxl-3" = "xxxl-3",
	"xxxl-4" = "xxxl-4",
	"xxxl-5" = "xxxl-5",
}
type KeysOfSpaceOptionsEnum = keyof typeof SpaceOptionsEnum;
export type SpaceOptionsObject = Pick<Size<Unit>, KeysOfSpaceOptionsEnum>;
export type SpaceOptions = keyof SpaceOptionsObject;
export type Space = SpaceOptionsObject;

/**
 * Type Guard. Call to determine if value is a SpaceOptions option.
 * @param value The value to check.
 * @returns Boolean true if value is an option, otherwise false.
 */
export function isSpaceOption(value: any): value is SpaceOptions {
	return Object.keys(SpaceOptionsEnum).includes(value);
}

/**
 * Theme
 * This is the interface that every theme object should implement. By augmenting DefaultTheme
 * we receive TypeScript support in our styled-components.
 */
export interface Theme {
	// Component specific themes

	components: {
		link: LinkTheme;
		pageSection: PageSectionTheme;
		ratingBadge: RatingBadgeTheme;
		signatureCard: SignatureCardTheme;
	};

	// Utils & global lookups

	borderWidths: BorderWidths;
	breakpoints: BreakpointAliasLegacy & Breakpoints;
	fonts: Fonts;
	fontSizes: number[]; // #Legacy
	fontWeights: FontWeightAliasLegacy & FontWeights;
	opacity: number[]; // #Legacy
	/*
  #Legacy: colors will eventually be replaced by palette, but to prevent legacy code breaking, both are needed.
  */
	colors: { [key in string]: string };
	palette: Palette;
	radii: Radii;
	shadows: string[] & ShadowAliasLegacy & Shadows;
	space: SpaceAliasLegacy & Space;
}
